import {Invitation, Registerer, RegistererState, SessionState, UserAgent} from "sip.js";

let userAgent = null;
let registerer = null;
let activeSession = null;
let activeEndpoint = null;
let selectedMode = "listen";
let output = null;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function settleWithin(promise, timeoutMs = 1500) {
  await Promise.race([Promise.resolve(promise), wait(timeoutMs)]);
}

async function stopRegistration() {
  const session = activeSession;
  activeSession = null;
  try {
    if (session?.state === SessionState.Established) {
      await settleWithin(session.bye());
    } else if (session && session.state !== SessionState.Terminated) {
      await settleWithin(session.reject({statusCode: 480}));
    }
  } catch (_) {}
  if (output) output.srcObject = null;
  try { await settleWithin(registerer?.unregister()); } catch (_) {}
  try { await settleWithin(userAgent?.stop()); } catch (_) {}
  registerer = null;
  userAgent = null;
  activeEndpoint = null;
}

function attachRemoteAudio(session) {
  const peer = session.sessionDescriptionHandler?.peerConnection;
  if (!peer || !output) return;
  const tracks = peer.getReceivers()
    .map((receiver) => receiver.track)
    .filter((track) => track?.kind === "audio");
  output.srcObject = new MediaStream(tracks);
  output.volume = 1;
  output.muted = false;
  output.play().catch(() => {
    window.dispatchEvent(new CustomEvent("dialstop-supervisor-audio", {
      detail: {status: "blocked", message: "Tap Start monitoring again to enable audio"},
    }));
  });
}

async function receiveInvite(invitation) {
  if (!(invitation instanceof Invitation) || activeSession) {
    await invitation.reject({statusCode: 486});
    return;
  }
  activeSession = invitation;
  invitation.stateChange.addListener((state) => {
    if (state === SessionState.Established) {
      const peer = invitation.sessionDescriptionHandler?.peerConnection;
      if (selectedMode === "listen") {
        peer?.getSenders().forEach((sender) => {
          if (sender.track?.kind === "audio") sender.track.enabled = false;
        });
      }
      attachRemoteAudio(invitation);
    }
    if (state === SessionState.Terminated) {
      if (output) output.srcObject = null;
      activeSession = null;
    }
  });
  await invitation.accept({
    sessionDescriptionHandlerOptions: {constraints: {audio: true, video: false}},
  });
}

async function connectOnce(config) {
  const uri = UserAgent.makeURI(`sip:${config.username}@${config.server}`);
  if (!uri || !config.websocket_url) throw new Error("Browser phone configuration is incomplete");
  const ua = new UserAgent({
    uri,
    displayName: "PBX Supervisor",
    authorizationUsername: config.auth_username,
    authorizationPassword: config.password,
    transportOptions: {server: config.websocket_url, connectionTimeout: 8},
    sessionDescriptionHandlerFactoryOptions: {
      peerConnectionConfiguration: {
        iceServers: Array.isArray(config.ice_servers) ? config.ice_servers : [],
      },
    },
    delegate: {onInvite: receiveInvite},
    logLevel: "warn",
  });
  const reg = new Registerer(ua, {expires: 1800, refreshFrequency: 70});
  userAgent = ua;
  registerer = reg;
  await ua.start();
  const registered = new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => reject(new Error("Browser phone registration timed out")), 7000);
    reg.stateChange.addListener((state) => {
      if (settled) return;
      if (state === RegistererState.Registered) {
        settled = true;
        clearTimeout(timer);
        resolve();
      } else if (state === RegistererState.Unregistered || state === RegistererState.Terminated) {
        settled = true;
        clearTimeout(timer);
        reject(new Error("Browser phone registration was rejected"));
      }
    });
  });
  await reg.register();
  await registered;
  activeEndpoint = config.endpoint;
}

async function connect(config, mode, audioElement) {
  selectedMode = mode || "listen";
  output = audioElement;
  const permission = await navigator.mediaDevices.getUserMedia({audio: true, video: false});
  permission.getTracks().forEach((track) => track.stop());
  if (userAgent && activeEndpoint === config.endpoint) return;
  await stopRegistration();
  let lastError = null;
  for (let attempt = 0; attempt < 8; attempt += 1) {
    try {
      await connectOnce(config);
      return;
    } catch (error) {
      lastError = error;
      await stopRegistration();
      await wait(500 + attempt * 350);
    }
  }
  throw lastError || new Error("Browser audio could not connect");
}

window.DialStopSupervisorPhone = {connect, disconnect: stopRegistration};
